import React, { useState, useEffect } from 'react';
import { QuizQuestion as QuestionType } from '../../ViewModels/QuizModel';
import { Button, ButtonGroup } from '@mui/material';
import TextField from '@mui/material/TextField';
import { Elements } from '@kontent-ai/delivery-sdk';
import RichTextComponents from '../RichTextComponents';
import '../../CSS/QuizExercise.css';
import ComponentTitle from '../ComponentTitle';

interface QuizExerciseProps {
  quizId?: string | undefined;
  element?: any | undefined;
}

const QuizExercise: React.FC<QuizExerciseProps> = props => {
  const [qid] = useState(crypto.randomUUID());

  const [questions, setQuestions] = useState<QuestionType[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [correctAnswer, setCorrectAnswer] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [feedBack, setFeedback] = useState<Elements.RichTextElement>({
    type: 'rich_text',
    value: '',
  } as Elements.RichTextElement);
  const [loading, setLoading] = useState(true);

  const [quizFITBValue, setFITBAnswerValue] = useState('');
  const [disableFitButton, setDisableFitButton] = useState(false);
  const [fitbBGColor, setFitbBGColor] = useState<string | null>(null);
  function handleFITBSubmit(): void {
    if (!questions[currentQuestion]) return;

    setDisableFitButton(true);
    setFitbBGColor('#ffbaba');
    questions[currentQuestion].options.forEach(item => {
      if (item.toLowerCase() === quizFITBValue.trim().toLowerCase()) {
        setScore(score + 1);
        setFitbBGColor('#b1fab1');
      }
    });
    setFeedback(questions[currentQuestion].correctFeedback);
  }

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFITBSubmit();
    }
  };
  const handleAnswerSelection = (answer: any): void => {
    if (!questions[currentQuestion]) return;

    setSelectedAnswer(answer);
    setCorrectAnswer(questions[currentQuestion].correctAnswer);
    if (answer === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
    setFeedback(questions[currentQuestion].correctFeedback);
  };
  useEffect(() => {
    const fetchQuiz = async (): Promise<void> => {
      let questions: any[] = [];
      let data = null;
      if (
        props.element !== undefined &&
        props.element !== null &&
        props.element.questions &&
        props.element.questions.linkedItems
      ) {
        data = { elements: props.element };
        data.elements.questions.linkedItems.forEach((item: any) => {
          if (item) {
            let q = new QuestionType();
            q.transformQuestionsFromKontent(item);
            questions.push(q);
          }
        });
        setQuestions(questions);
        setLoading(false);
      } else {
        setLoading(false);
      }
      setQuestions(questions);
      setLoading(false);
    };
    fetchQuiz();
  }, [props.element, props.quizId]);

  const handleNextQuestion = (): void => {
    /// reset all the things
    setDisableFitButton(false);
    setFitbBGColor(null);
    setSelectedAnswer(null);
    setCorrectAnswer(null);
    setFITBAnswerValue('');
    setFeedback({ type: 'rich_text', value: '' } as Elements.RichTextElement);
    // go to the next question
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const [quizCompleted, setQuizCompleted] = useState(false);
  const handleFinishQuiz = (): void => {
    setQuizCompleted(true);
  };

  if (questions.length === 0 && !loading) {
    return <div>No quiz questions available.</div>;
  }

  if (loading) {
    return <div>Loading quiz questions...</div>;
  }

  return (
    <div className="quiz-container da-panel-primary">
      {props.element.title && props.element.title.value && (
        <ComponentTitle title={props.element.title.value} />
      )}
      {!quizCompleted && questions.length > 0 && (
        <div id={'quizMain-' + qid}>
          {' '}
          <div id="quizSection">
            <div id="quizQuestions">
              {props.element &&
                props.element.type !== 'single_question_quiz_exercise' && (
                  <div style={{ textAlign: 'left' }} className="da-title">
                    <h2>Question {currentQuestion + 1}</h2>
                  </div>
                )}
              <div
                style={{
                  textAlign: 'left',
                  fontSize: '1.0em',
                  wordBreak: 'break-word',
                  lineHeight: '21px',
                  wordWrap: 'break-word',
                  marginTop: '15px',
                }}
              >
                {questions[currentQuestion]?.questionText && (
                  <p>{questions[currentQuestion].questionText}</p>
                )}
                <br />
              </div>
              <div>
                {questions[currentQuestion]?.questionType !==
                  'fill_in_the_blank_question' &&
                  questions[currentQuestion]?.options && (
                    <ButtonGroup
                      key="quizButtons"
                      orientation="vertical"
                      aria-label="Vertical button group"
                      fullWidth
                    >
                      {questions[currentQuestion].options.map(answer => (
                        <Button
                          key={answer}
                          sx={{
                            fontSize: '1.0em',
                            fontFamily: 'Open Sans, sans-serif',
                            wordBreak: 'break-word',
                            lineHeight: '21px',
                            wordWrap: 'break-word',
                            backgroundColor: selectedAnswer
                              ? answer === correctAnswer
                                ? '#b1fab1'
                                : answer === selectedAnswer
                                  ? '#ffbaba'
                                  : undefined
                              : undefined,
                            color: selectedAnswer
                              ? answer === correctAnswer
                                ? '#00ff00'
                                : answer === selectedAnswer
                                  ? '#ff0000'
                                  : undefined
                              : undefined,
                          }}
                          onClick={(): void => handleAnswerSelection(answer)}
                          disabled={selectedAnswer !== null}
                        >
                          {answer}
                        </Button>
                      ))}
                    </ButtonGroup>
                  )}
                {questions[currentQuestion]?.questionType ===
                  'fill_in_the_blank_question' && (
                  <div className="quiz-answer-textinput">
                    <TextField
                      id="quiz-fill-in-the-blank-answer"
                      variant="outlined"
                      size="medium"
                      sx={{
                        width: '50%',
                        padding: '0px',
                        backgroundColor: fitbBGColor,
                      }}
                      inputProps={{
                        style: { fontSize: '1.5em', padding: '14px' },
                      }}
                      onKeyPress={onKeyPress}
                      value={quizFITBValue}
                      disabled={disableFitButton}
                      onChange={(e): void => setFITBAnswerValue(e.target.value)}
                    />
                    <Button
                      sx={{
                        fontSize: '1.25em',
                      }}
                      variant="contained"
                      onClick={handleFITBSubmit}
                      disabled={disableFitButton}
                    >
                      Check
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="quiz-feedback">
              {feedBack && feedBack.value ? (
                <RichTextComponents element={feedBack} />
              ) : null}
            </div>
          </div>{' '}
          {props.element?.type !== 'single_question_quiz_exercise' &&
            questions.length > 0 && (
              <div style={{ textAlign: 'center' }}>
                {currentQuestion < questions.length - 1 && (
                  <Button
                    sx={{ fontSize: '1.25em' }}
                    onClick={(): void => handleNextQuestion()}
                  >
                    Next Question
                  </Button>
                )}
                {currentQuestion === questions.length - 1 && (
                  <Button
                    sx={{ fontSize: '1em' }}
                    onClick={(): void => handleFinishQuiz()}
                  >
                    Finish Quiz
                  </Button>
                )}
                <p>Score: {score}</p>
              </div>
            )}
        </div>
      )}{' '}
      {props.element?.type !== 'single_question_quiz_exercise' &&
        quizCompleted &&
        questions.length > 0 && (
          <div
            id={'quiz-end-' + qid}
            style={{ height: '300px', padding: '10px' }}
          >
            <h2>Quiz Completed.</h2>
            <br />
            <h3>
              You scored {score} out of {questions.length}.
            </h3>
            <br />
          </div>
        )}
    </div>
  );
};

export default QuizExercise;
